import { useQuery, type QueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import type { components } from '../api/schema';
import { unwrap } from '../api/unwrap';
import { buildListQuerySerializer } from '../lib/list-query';

import type { CampaignRecord } from './queries';

// Plan 08-03 — campaign-scoped audit events. Backend lives on the typed
// `/campaigns/{id}/events` endpoint with bracketed filter/sort/page (UX-02 +
// ADR-014). Polls fast (5s) while the campaign is live, otherwise idles
// (30s); paused in background per UX standard.

export type CampaignEventDto = components['schemas']['CampaignEvent'];

// Mirrors the backend `CAMPAIGN_EVENT_GROUPS` taxonomy from
// `packages/backend/src/campaigns/events.ts`. Kept in lockstep manually —
// rare additions (Phase 8.5-03 added `campaign_audience_snapshotted`) are
// folded back when noticed.
export const CAMPAIGN_EVENT_GROUPS = {
  lifecycle: [
    'campaign_created',
    'campaign_updated',
    'campaign_deleted',
    'campaign_launched',
    'campaign_paused',
    'campaign_resumed',
    'campaign_cancelled',
    'campaign_completed',
    'campaign_destroyed',
  ],
  items: [
    'campaign_items_added',
    'campaign_item_deleted',
    'campaign_item_requeued',
    'campaign_item_skipped_max_retries',
    'campaign_item_duplicate_guard_skip',
  ],
  snapshot: [
    'campaign_snapshot_started',
    'campaign_snapshot_completed',
    'campaign_snapshot_item_resolved',
    'campaign_snapshot_item_failed',
    'campaign_audience_snapshotted',
  ],
  driver: ['campaign_driver_tick', 'campaign_auto_enqueued'],
  trades: ['trade_cancelled_by_campaign'],
  recipients: [
    'campaign_recipient_quarantined',
    'campaign_recipient_resolved',
    'campaign_recipient_retried',
  ],
} as const satisfies Record<string, readonly string[]>;

export type CampaignEventGroup = keyof typeof CAMPAIGN_EVENT_GROUPS;
export const CAMPAIGN_EVENT_GROUP_KEYS = Object.keys(CAMPAIGN_EVENT_GROUPS) as CampaignEventGroup[];

export const expandGroups = (groups: readonly CampaignEventGroup[]): string[] => {
  if (groups.length === 0) return [];
  const out = new Set<string>();
  for (const g of groups) for (const t of CAMPAIGN_EVENT_GROUPS[g]) out.add(t);
  return [...out];
};

export interface CampaignEventsListResult {
  items: CampaignEventDto[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CampaignEventsListInput {
  page: number;
  pageSize: number;
  groups: readonly CampaignEventGroup[];
}

const isLive = (status: CampaignRecord['status'] | undefined): boolean =>
  status === 'running' || status === 'paused';

export const eventsKeys = {
  list: (campaignId: string, input: CampaignEventsListInput) =>
    ['campaigns', 'one', campaignId, 'events', input] as const,
};

export const useCampaignEvents = (
  campaignId: string,
  input: CampaignEventsListInput,
  status: CampaignRecord['status'] | undefined,
) =>
  useQuery({
    queryKey: eventsKeys.list(campaignId, input),
    queryFn: async (): Promise<CampaignEventsListResult> => {
      const expanded = expandGroups(input.groups);
      const filter: { types?: string } = {};
      if (expanded.length > 0) filter.types = expanded.join(',');
      const result = await api.GET('/api/campaigns/{id}/events', {
        params: {
          path: { id: campaignId },
          query: {
            sort: '-occurredAt',
            page: String(input.page),
            pageSize: String(input.pageSize),
          },
        },
        querySerializer: buildListQuerySerializer(filter),
      });
      return unwrap(result) as CampaignEventsListResult;
    },
    refetchInterval: isLive(status) ? 5_000 : 30_000,
    refetchIntervalInBackground: false,
  });

export const invalidateCampaignEvents = (qc: QueryClient, campaignId: string): Promise<void> =>
  qc.invalidateQueries({ queryKey: ['campaigns', 'one', campaignId, 'events'] });
